/**
 * Module identifiers and PostgreSQL schema names.
 *
 * Every business module has exactly one corresponding schema, and the names
 * match 1:1. This makes the system easy to reason about: if you see code
 * reading `validation.*`, you know it belongs to the validation module.
 *
 * The `SHARED` schema is special: every module has read-only access to it.
 * It contains lookup tables used platform-wide (currencies, products,
 * branches, document types, severity levels, audit cycles).
 */
export const MODULE = {
    AUTH: 'auth',
    ACCESS: 'access',
    INGESTION: 'ingestion',
    PARSING: 'parsing',
    VALIDATION: 'validation',
    SCORING: 'scoring',
    DETECTION: 'detection',
    REPORTING: 'reporting',
    EVIDENCE: 'evidence',
    ADMIN: 'admin',
    MASTERS: 'masters',
    SETTINGS: 'settings',
    NOTIFICATIONS: 'notifications',
};
export const SHARED_SCHEMA = 'shared';
export const ALL_MODULES = Object.values(MODULE);
// -------------------------------------------------------------------------
// Severity levels for findings. Numerical values allow sorting and
// aggregation. Do not renumber once in production — scoring formulas may
// depend on these values.
// -------------------------------------------------------------------------
export const SEVERITY = {
    CRITICAL: 'critical',
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
    INFO: 'info',
};
export const SEVERITY_WEIGHT = {
    critical: 100,
    high: 50,
    medium: 20,
    low: 5,
    info: 0,
};
/** Severities in canonical display order (highest severity first). */
export const SEVERITIES_ORDERED = [
    SEVERITY.CRITICAL,
    SEVERITY.HIGH,
    SEVERITY.MEDIUM,
    SEVERITY.LOW,
    SEVERITY.INFO,
];
// -------------------------------------------------------------------------
// Finding status — the lifecycle of a validation finding.
//
// Sprint 6 workflow: OPEN → ACCEPTED | WAIVED | FALSE_POSITIVE. A finding
// in an earlier run is marked SUPERSEDED when a fresh validation run
// produces a successor.
// -------------------------------------------------------------------------
export const FINDING_STATUS = {
    OPEN: 'open',
    ACCEPTED: 'accepted',
    WAIVED: 'waived',
    FALSE_POSITIVE: 'false_positive',
    SUPERSEDED: 'superseded',
};
// -------------------------------------------------------------------------
// Ingestion job status — file-level lifecycle.
// -------------------------------------------------------------------------
export const JOB_STATUS = {
    RECEIVED: 'received',
    QUEUED: 'queued',
    PARSING: 'parsing',
    PARSED: 'parsed',
    VALIDATING: 'validating',
    VALIDATED: 'validated',
    SCORING: 'scoring',
    SCORED: 'scored',
    COMPLETED: 'completed',
    FAILED: 'failed',
};
// -------------------------------------------------------------------------
// Document types the system recognizes. Adding a new type here requires:
// 1. A classifier prompt update (in masters)
// 2. An optional field extractor in the parsing module
// 3. A document checklist entry in the relevant product master
// -------------------------------------------------------------------------
export const DOCUMENT_TYPE = {
    SANCTION_LETTER: 'sanction_letter',
    KFS: 'kfs',
    LOAN_AGREEMENT: 'loan_agreement',
    CAM: 'cam',
    KYC: 'kyc',
    CKYC: 'ckyc',
    ITR: 'itr',
    FORM_26AS: 'form_26as',
    BANK_STATEMENT: 'bank_statement',
    SALARY_SLIP: 'salary_slip',
    CIBIL: 'cibil',
    VALUATION: 'valuation',
    INSURANCE: 'insurance',
    NOC: 'noc',
    STAMP_PAPER: 'stamp_paper',
    REGISTRATION_RECEIPT: 'registration_receipt',
    GUARANTEE_DOC: 'guarantee_doc',
    BOARD_RESOLUTION: 'board_resolution',
    PROJECT_REPORT: 'project_report',
    COVENANT_SCHEDULE: 'covenant_schedule',
    SECURITY_TRUSTEE_DOC: 'security_trustee_doc',
    STOCK_STATEMENT: 'stock_statement',
    DEBTOR_AGEING: 'debtor_ageing',
    // ---- 17-point checklist additions (Batch 1) ----
    CFA: 'cfa', // Credit File Analysis sheet
    LEGAL_TECHNICAL_REPORT: 'legal_technical_report', // Combined L&T report
    BQS_REPORT: 'bqs_report', // Business / Borrower Quality Score report
    OTHER: 'other',
    UNCLASSIFIED: 'unclassified',
};
//# sourceMappingURL=modules.js.map